import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mealCompositionService } from '@/services/mealComposition';
import { useCompositionRecipes } from '@/hooks/useCompositionRecipes';
import { PageLayout } from '@/components/layout';
import MealCompositionForm from '@/components/meal-composition/MealCompositionForm';
import { Skeleton } from '@/components/ui/skeleton';
import { MEAL_COMPOSITIONS } from '@/constants/queryKeys';
import { toast } from 'sonner';

const EditComposition: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: composition, isLoading } = useQuery({
    queryKey: ['meal-composition', id],
    queryFn: () => mealCompositionService.getMealCompositionById(id!),
    enabled: !!id
  });

  const { recipes } = useCompositionRecipes();

  const updateMutation = useMutation({
    mutationFn: (data: any) => 
      mealCompositionService.updateMealComposition(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEAL_COMPOSITIONS] });
      queryClient.invalidateQueries({ queryKey: ['meal-composition', id] });
      toast.success('Composition updated successfully');
      navigate(`/composition/${id}`);
    },
    onError: () => {
      toast.error('Failed to update composition');
    }
  });

  const handleSubmit = async (data: any) => {
    await updateMutation.mutateAsync(data);
  };

  const handleCancel = () => {
    navigate(`/composition/${id}`);
  };


  if (isLoading) {
    return (
      <PageLayout 
        title="Edit Composition"
      >
        <div className="space-y-6">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </PageLayout>
    );
  }

  if (!composition) {
    return (
      <PageLayout 
        title="Composition Not Found"
      >
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            The composition you're looking for doesn't exist.
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Edit Composition"
      description={`Update "${composition.name}"`}
    >
      <MealCompositionForm
        composition={composition}
        recipes={recipes}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={updateMutation.isPending}
      />
    </PageLayout>
  );
};

export default EditComposition;